import React, { useState } from 'react';
import {
  Table, Button, Modal, Form, FormItem, Input, TextArea, Card, useToast, showConfirm
} from '../ui';
import type { Column } from '../ui';
import {
  PlusIcon, PencilIcon, TrashIcon, BuildingStorefrontIcon, MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { Branch } from '../config/supabase';
import { useBranches, useCreateBranch, useUpdateBranch, useDeleteBranch } from '../hooks/api/useBranches';

interface BranchFormData {
  name: string;
  description?: string;
}

const BranchManagement: React.FC = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingBranch, setEditingBranch] = useState<Branch | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');

  const { success, error: showError } = useToast();

  const {
    data: branchesData,
    isLoading: loading,
    error: branchesError
  } = useBranches(currentPage, pageSize, searchTerm);

  const createBranchMutation = useCreateBranch();
  const updateBranchMutation = useUpdateBranch();
  const deleteBranchMutation = useDeleteBranch();

  const branches = branchesData?.branches || [];
  const totalCount = branchesData?.totalCount || 0;

  React.useEffect(() => {
    if (branchesError) {
      showError('Error fetching branches: ' + branchesError.message);
    }
  }, [branchesError, showError]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handlePageChange = (page: number, newPageSize?: number) => {
    setCurrentPage(page);
    if (newPageSize && newPageSize !== pageSize) {
      setPageSize(newPageSize);
      setCurrentPage(1);
    }
  };

  const handleAdd = () => {
    setEditingBranch(null);
    setModalVisible(true);
  };

  const handleEdit = (branch: Branch) => {
    setEditingBranch(branch);
    setModalVisible(true);
  };

  const handleDelete = async (id: string, branchName: string) => {
    const confirmed = await showConfirm({
      title: `Delete branch "${branchName}"?`,
      content: 'This action cannot be undone.',
      onConfirm: () => {},
      type: 'danger'
    });

    if (!confirmed) return;

    try {
      await deleteBranchMutation.mutateAsync(id);
      success('Branch deleted successfully');
    } catch (error: any) {
      showError('Error deleting branch: ' + error.message);
    }
  };

  const handleSubmit = async (values: BranchFormData) => {
    try {
      if (editingBranch) {
        await updateBranchMutation.mutateAsync({
          id: editingBranch.id,
          ...values
        });
        success('Branch updated successfully');
      } else {
        await createBranchMutation.mutateAsync(values);
        success('Branch created successfully');
      }

      handleModalClose();
    } catch (error: any) {
      showError('Error saving branch: ' + error.message);
    }
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingBranch(null);
  };

  const columns: Column<Branch>[] = [
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      sorter: (a: Branch, b: Branch) => a.name.localeCompare(b.name),
      render: (name: string) => <span className="font-semibold text-gray-900">{name}</span>,
    },
    {
      key: 'description',
      title: 'Description',
      dataIndex: 'description',
      render: (description: string) => (
        <span className="text-gray-600">
          {description || 'No description'}
        </span>
      ),
    },
    {
      key: 'created_at',
      title: 'Created',
      dataIndex: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      key: 'actions',
      title: 'Actions',
      width: 120,
      render: (_, record: Branch) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(record)}
            className="text-blue-600 hover:text-blue-800"
          >
            <PencilIcon className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              showConfirm({
                title: 'Delete Branch',
                content: `Are you sure you want to delete "${record.name}"? This action cannot be undone.`,
                onConfirm: () => handleDelete(record.id, record.name),
                confirmText: 'Delete',
                type: 'danger'
              });
            }}
            className="text-red-600 hover:text-red-800"
          >
            <TrashIcon className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <BuildingStorefrontIcon className="h-6 w-6 mr-2" />
          Branch Management
        </h1>
        <p className="text-gray-600 mt-1">
          Manage store branches and their information
        </p>
      </div>

      <div className="flex justify-between items-center">
        <Button
          variant="primary"
          size="lg"
          onClick={handleAdd}
          className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 font-semibold px-6 py-3"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add New Branch
        </Button>

        <div className="ml-auto">
          <Input
            placeholder="Search branches by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            leftIcon={<MagnifyingGlassIcon className="h-4 w-4" />}
            className="w-80"
          />
        </div>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={branches}
          loading={loading}
          rowKey="id"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: handlePageChange,
            onShowSizeChange: handlePageChange
          }}
        />
      </Card>

      <Modal
        isOpen={modalVisible}
        onClose={handleModalClose}
        title={editingBranch ? 'Edit Branch' : 'Add New Branch'}
        size="md"
      >
        <Form
          onSubmit={handleSubmit}
          initialValues={editingBranch || {}}
        >
          <div className="space-y-4">
            <FormItem name="name" label="Branch Name" required>
              <Input
                placeholder="Enter branch name"
                autoFocus
                maxLength={100}
              />
            </FormItem>

            <FormItem name="description" label="Description">
              <TextArea
                placeholder="Enter branch description..."
                rows={4}
                maxLength={500}
              />
            </FormItem>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Button variant="outline" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              {editingBranch ? 'Update Branch' : 'Add Branch'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default BranchManagement;
